import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Loader2, Mail, KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { APP_NAME, APP_FULL } from "@/lib/constants";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: otp + newpw, 3: done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestOtp = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      // Server SELALU return 200 (anti-enumeration). Kita tidak bisa memastikan
      // email terkirim ke user dari response. Beri pesan netral.
      toast.success(data.message || "Bila email terdaftar, kode OTP dikirim.");
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 8) { setError("Password baru minimal 8 karakter"); return; }
    if (newPw !== confirmPw) { setError("Konfirmasi password tidak sama"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        new_password: newPw,
      });
      setStep(3);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-background">
      <div className="relative hidden lg:flex lg:col-span-3 flex-col justify-between text-white overflow-hidden">
        <div className="absolute inset-0" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1671882865872-3f3740f95e77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxwYXB1YSUyMG5hdHVyZSUyMGZvcmVzdCUyMGxhbmRzY2FwZSUyMHN1bnJpc2V8ZW58MHx8fHwxNzg3OTcyNTI5fDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#17365D]/95 via-[#17365D]/85 to-[#276AA3]/75" />
        <div className="absolute inset-0 papua-motif opacity-30" />
        <div className="relative z-10 p-12">
          <div className="flex items-center gap-3">
            <div className="relative h-14 w-14 rounded-xl bg-white text-primary flex items-center justify-center font-heading text-2xl font-bold shadow-lg">
              S<div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-accent border-2 border-white" />
            </div>
            <div>
              <div className="text-[0.7rem] tracking-[0.22em] uppercase opacity-90 font-semibold">Klasis Mimika</div>
              <div className="font-heading font-bold text-2xl leading-none mt-1">{APP_NAME}</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 p-12 space-y-6 max-w-xl">
          <div className="h-1 w-24 bg-accent rounded-full" />
          <h1 className="font-heading font-bold text-4xl xl:text-5xl leading-[1.05]">
            Reset<br />
            <span className="text-accent">Kata Sandi</span><br />
            dengan Aman.
          </h1>
          <p className="text-sm opacity-90 leading-relaxed">
            Kami akan mengirim kode OTP 6-digit ke email Anda. Kode berlaku 15 menit dan hanya bisa dipakai sekali.
          </p>
        </div>
        <div className="relative z-10 p-12 text-[0.7rem] opacity-70 tracking-wide">
          © 2026 SIMPELKAMI GPDP
        </div>
      </div>

      <div className="lg:col-span-2 flex flex-col p-6 sm:p-12">
        <div className="lg:hidden flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold">S</div>
          <div>
            <div className="font-heading font-bold text-primary">{APP_NAME}</div>
            <div className="text-xs text-muted-foreground">{APP_FULL}</div>
          </div>
        </div>

        <div className="my-auto max-w-md w-full mx-auto">
          <Link to="/login" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mb-4" data-testid="link-back-login">
            <ArrowLeft className="h-3 w-3" /> Kembali ke Login
          </Link>

          {step === 1 && (
            <>
              <div className="text-xs tracking-[0.2em] uppercase text-accent font-bold mb-2">Lupa Kata Sandi</div>
              <h2 className="font-heading font-bold text-3xl text-foreground mb-2">Reset dengan OTP.</h2>
              <p className="text-sm text-muted-foreground mb-3">Masukkan email akun Anda. Kami akan mengirim kode OTP 6-digit ke email tersebut.</p>
              <div className="text-xs bg-muted/40 border border-border rounded-md p-2.5 mb-6 text-muted-foreground">
                <strong className="text-foreground">Catatan:</strong> Email harus sudah didaftarkan oleh Admin Klasis. Bila email belum terdaftar, kode OTP tidak akan dikirim. Hubungi Sekretariat Klasis.
              </div>
              <form onSubmit={requestOtp} className="space-y-4" data-testid="forgot-form-step1">
                <div className="space-y-2">
                  <Label htmlFor="email"><Mail className="h-3.5 w-3.5 inline-block mr-1" /> Email</Label>
                  <Input
                    id="email" type="email" autoComplete="email" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="nama@example.local"
                    data-testid="input-forgot-email"
                  />
                </div>
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2" data-testid="forgot-error">{error}</div>
                )}
                <Button type="submit" className="w-full h-11 rounded-full" disabled={loading} data-testid="btn-send-otp">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Kirim Kode OTP
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-xs tracking-[0.2em] uppercase text-accent font-bold mb-2">Cek Inbox &amp; Spam</div>
              <h2 className="font-heading font-bold text-3xl text-foreground mb-2">Cek Email Anda.</h2>
              <p className="text-sm text-muted-foreground mb-2">
                Bila email <strong>{email}</strong> terdaftar, kode 6-digit dikirim dalam beberapa detik. Berlaku 15 menit &amp; hanya sekali pakai.
              </p>
              <p className="text-xs text-muted-foreground mb-6">Belum menerima setelah 2 menit? Cek folder <strong>Spam / Promosi</strong>, atau <button className="text-primary hover:underline" onClick={()=>setStep(1)} data-testid="btn-retry-email">ubah email</button>. Bila email belum terdaftar, hubungi Admin Klasis.</p>
              <form onSubmit={resetPw} className="space-y-4" data-testid="forgot-form-step2">
                <div className="space-y-2">
                  <Label htmlFor="otp"><KeyRound className="h-3.5 w-3.5 inline-block mr-1" /> Kode OTP (6 digit)</Label>
                  <Input
                    id="otp" type="text" inputMode="numeric" maxLength={6} required autoFocus
                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g,""))}
                    placeholder="123456"
                    className="text-center font-mono text-2xl tracking-[0.5em]"
                    data-testid="input-otp"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newpw">Kata Sandi Baru (min. 8 karakter)</Label>
                  <div className="relative">
                    <Input
                      id="newpw" type={showPw ? "text" : "password"} required minLength={8}
                      value={newPw} onChange={(e) => setNewPw(e.target.value)}
                      className="pr-10"
                      data-testid="input-newpw"
                    />
                    <button type="button" onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmpw">Konfirmasi Kata Sandi Baru</Label>
                  <Input
                    id="confirmpw" type={showPw ? "text" : "password"} required minLength={8}
                    value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                    data-testid="input-confirmpw"
                  />
                </div>
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2" data-testid="forgot-error-step2">{error}</div>
                )}
                <Button type="submit" className="w-full h-11 rounded-full" disabled={loading} data-testid="btn-reset-password">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Reset Kata Sandi
                </Button>
              </form>
            </>
          )}

          {step === 3 && (
            <div className="text-center py-8" data-testid="forgot-success">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="font-heading font-bold text-2xl mb-2">Kata Sandi Diperbarui!</h2>
              <p className="text-sm text-muted-foreground mb-6">Anda sekarang bisa login dengan kata sandi baru.</p>
              <Button onClick={()=>navigate("/login")} className="rounded-full" data-testid="btn-goto-login">
                Menuju Halaman Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
